import jsQR from 'jsqr';

export interface QRScanResult {
  success: boolean;
  data: string | null;
  attempts: number;
  method: 'direct' | 'enhanced' | 'inverted' | 'none';
}

/**
 * Scans an image for a QR code and returns the data.
 * Optimized for document images (IDs) with multiple retry strategies.
 *
 * Handles:
 * - Direct QR scanning
 * - Grayscale + contrast enhancement
 * - Inverted colors (for dark backgrounds)
 * - Multiple attempts with different filters
 */
export async function scanQRCode(file: File): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const result = scanQRFromImage(img);
            resolve(result.data);
        };
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(file);
    });
}

/**
 * Internal function to scan QR code with multiple strategies
 */
function scanQRFromImage(img: HTMLImageElement): QRScanResult {
    let attempts = 0;

    // Strategy 1: Direct scan (no filters)
    attempts++;
    let code = scanWithCanvas(img, null);
    if (code) {
        return { success: true, data: code, attempts, method: 'direct' };
    }

    // Strategy 2: Grayscale + contrast enhancement
    attempts++;
    code = scanWithCanvas(img, 'grayscale(100%) contrast(150%)');
    if (code) {
        return { success: true, data: code, attempts, method: 'enhanced' };
    }

    // Strategy 3: Inverted colors (for dark backgrounds)
    attempts++;
    code = scanWithCanvas(img, 'invert(100%)');
    if (code) {
        return { success: true, data: code, attempts, method: 'inverted' };
    }

    // Strategy 4: Brightness + contrast
    attempts++;
    code = scanWithCanvas(img, 'brightness(110%) contrast(120%)');
    if (code) {
        return { success: true, data: code, attempts, method: 'enhanced' };
    }

    return { success: false, data: null, attempts, method: 'none' };
}

/**
 * Scan QR code from canvas with optional filter
 */
function scanWithCanvas(img: HTMLImageElement, filter: string | null): string | null {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = img.width;
    canvas.height = img.height;

    if (filter) {
        ctx.filter = filter;
    }

    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
    });

    return code ? code.data : null;
}
